import { useTranslation } from '@jobcost-monorepo/i18n';
import { Alert, Icon, Loader, useAuth } from '@jobcost-monorepo/ui';
import React, {
  ReactElement,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import InvoiceFormButtons from './Buttons';
import InvoiceFormTitle from './Title';
import {
  IDetailToInvoice,
  IDetailToInvoiceFetchParams,
  IDetailToInvoiceOrderWithHours,
  IResponseDetailToInvoice,
  ITimesheetsJobToInvoice,
} from '../types';
import {
  ApiResponse,
  CustomApiClient,
  handleAxiosError,
  handleGlobalError,
  IActivity,
  IGestifyCredential,
  IListActivityFetchParams,
  IListGestifyCredentialFetchParams,
  IListResponse,
  INVOICING_REQUEST_TIMEOUT_MS,
  MN_ACTIVITIES,
  MN_GESTIFY_CREDENTIALS,
} from '@jobcost-monorepo/crud';
import { useInvoicingConfig } from '@jobcost-monorepo/utils';
import {
  describeTimesheetInvoiceError,
  hasInvoicedOrders,
  ITimesheetInvoiceRequest,
  ITimesheetInvoiceResponse,
  TIMESHEET_INVOICES_URL,
} from './previxioInvoice';
import InvoiceFormBody from './Body';
import {
  createGestifyInvoice,
  DocumentoRigaCreateType,
} from '@jobcost-monorepo/gestify';
import dayjs from '@jobcost-monorepo/dayjs-config';
import { createRepository } from '@jobcost-monorepo/crud/dto/repositoryFactory';
import { AxiosError } from 'axios';

interface IInvoiceFormProps {
  setInvoiceFormOpen: (
    value: SetStateAction<ITimesheetsJobToInvoice | undefined>,
  ) => void;
  jobToInvoice: ITimesheetsJobToInvoice;
  startDate: string;
  endDate: string;
}

const InvoiceForm = ({
  setInvoiceFormOpen,
  jobToInvoice,
  startDate,
  endDate,
}: IInvoiceFormProps): ReactElement => {
  const { t } = useTranslation(['timesheets', 'commons']);
  const { user } = useAuth();
  const orderReferenceLabel = t('invoice.orderReferenceLabel');
  const ofLabel = t('invoice.ofLabel');
  const technologyLabel = t('invoice.technologyLabel');

  const [showPopup, setShowPopup] = React.useState(false);
  const [popupMessage, setPopupMessage] = useState<string>(t('recordSaved'));
  const [loading, setLoading] = useState<boolean>(false);
  const [detailToInvoice, setDetailToInvoice] = useState<IDetailToInvoice>();
  const {
    config: invoicingConfig,
    error: invoicingConfigError,
    reload: reloadInvoicingConfig,
  } = useInvoicingConfig();
  const alreadyInvoiced = hasInvoicedOrders(detailToInvoice);
  const getSubmitDisabledReason = (): string | undefined => {
    if (invoicingConfigError) return t('commons:invoicingConfig.error');
    if (alreadyInvoiced) return t('invoice.alreadyInvoiced');
    return undefined;
  };
  const submitDisabledReason = getSubmitDisabledReason();

  const handleCloseForm = () => {
    setInvoiceFormOpen(undefined);
  };

  const getDataToInvoice = async () => {
    if (!jobToInvoice) return;

    setLoading(true);

    try {
      const jobId = Number(jobToInvoice.jobId);
      const resourceId = Number(jobToInvoice.resourceId);

      const props: IDetailToInvoiceFetchParams = {
        jobId,
        resourceId,
        startDate,
        endDate,
      };

      //Ricava il totale lavorato per ciascun ordine nel periodo di fatturazione
      const customApiClient = new CustomApiClient();
      const response = await customApiClient.getWithParams<
        IResponseDetailToInvoice,
        IDetailToInvoiceFetchParams
      >('timesheets/getDetailsToInvoice', props);

      if (response.succeeded) {
        setDetailToInvoice(response.data?.data);
      }
    } catch (e) {
      console.log(e);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (showPopup) {
      setTimeout(() => {
        setShowPopup(false);
      }, 3000);
    }
  }, [showPopup]);

  console.log({ jobToInvoice }, startDate);

  useEffect(() => {
    getDataToInvoice().then();
  }, []);

  const handlePrevixioSubmit = async () => {
    if (!detailToInvoice) {
      handleGlobalError({
        code: 999,
        message: t('invoice.title'),
        description: t('invoice.errors.noDetailsInvoice'),
        responseMessage: t('invoice.errors.noDetailsInvoice'),
      });
      return;
    }

    setLoading(true);
    try {
      // Con Previxio l'orchestrazione è nel backend: il browser invia solo il periodo
      const request: ITimesheetInvoiceRequest = {
        jobId: Number(jobToInvoice.jobId),
        resourceId: Number(jobToInvoice.resourceId),
        startDate,
        endDate,
      };
      const response = await new CustomApiClient().create<
        ITimesheetInvoiceRequest | ITimesheetInvoiceResponse
      >(TIMESHEET_INVOICES_URL, request, INVOICING_REQUEST_TIMEOUT_MS);
      const invoice = response?.data as ITimesheetInvoiceResponse | undefined;
      setPopupMessage(
        t('invoice.previxio.success', {
          number: invoice?.externalInvoiceNumber ?? invoice?.externalInvoiceId,
        }),
      );
      setShowPopup(true);
    } catch (error) {
      const description = describeTimesheetInvoiceError(error, t);
      handleGlobalError({
        code: (error as AxiosError)?.response?.status ?? 999,
        message: t('invoice.title'),
        description,
        responseMessage: description,
      });
    }
    await getDataToInvoice();
    setLoading(false);
  };

  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    if (!invoicingConfig) {
      handleGlobalError({
        code: 999,
        message: t('invoice.title'),
        description: t('commons:invoicingConfig.error'),
        responseMessage: t('commons:invoicingConfig.error'),
      });
      return;
    }

    if (invoicingConfig.provider === 'previxio') {
      await handlePrevixioSubmit();
      return;
    }

    // Recupera i dati dell'utente loggato
    if (!user) {
      handleGlobalError({
        code: 999,
        message: t('invoice.title'),
        description: t('invoice.errors.noUserData'),
        responseMessage: t('invoice.errors.noUserData'),
      });
      setLoading(false);
      return;
    }

    setLoading(true);

    if (!detailToInvoice) {
      handleGlobalError({
        code: 999,
        message: t('invoice.title'),
        description: t('invoice.errors.noDetailsInvoice'),
        responseMessage: t('invoice.errors.noDetailsInvoice'),
      });
      setLoading(false);
      return;
    }

    if (!detailToInvoice.customer.externalId) {
      handleGlobalError({
        code: 999,
        message: t('invoice.title'),
        description: t('invoice.errors.noCustomerExternalId'),
        responseMessage: t('invoice.errors.noCustomerExternalId'),
      });
      setLoading(false);
      return;
    }

    // Recupera le credenziali Gestify dell'utente
    const repository = createRepository<
      IGestifyCredential,
      IListGestifyCredentialFetchParams
    >(MN_GESTIFY_CREDENTIALS);
    const gestifyCredentialResponse: ApiResponse<
      IListResponse<IGestifyCredential>
    > = await repository.getMany({ userId: user.id });
    if (
      !gestifyCredentialResponse ||
      !gestifyCredentialResponse?.data ||
      gestifyCredentialResponse?.data?.data?.length !== 1
    ) {
      handleGlobalError({
        code: 999,
        message: t('invoice.title'),
        description: t('invoice.errors.invoiceError'),
        responseMessage: t('invoice.errors.invoiceError'),
      });
      setLoading(false);
      return;
    }

    // Crea la fattura in Gestify
    const gestifyCredential = gestifyCredentialResponse?.data.data[0];

    const gestifyInvoiceResponse = await createGestifyInvoice({
      username: gestifyCredential?.username,
      password: gestifyCredential?.password,
      externalId: detailToInvoice.customer.externalId,
      righe: detailToInvoice.ordersWithHours.flatMap((order) =>
        getRowFromOrder(order),
      ),
      dataDoc: dayjs.utc(endDate).endOf('month').format('YYYY-MM-DD'),
    });

    if (!gestifyInvoiceResponse) {
      handleGlobalError({
        code: 999,
        message: t('invoice.title'),
        description: t('invoice.errors.invoiceError'),
        responseMessage: t('invoice.errors.invoiceError'),
      });
      setLoading(false);
      return;
    }

    // Setta tutte le attività come fatturate
    const activities = await Promise.all(
      detailToInvoice.ordersWithHours.map(async (orderWithHours) => {
        const repository = createRepository<
          IActivity,
          IListActivityFetchParams
        >(MN_ACTIVITIES);

        const activityToCreate = {
          jobId: orderWithHours.order.jobId,
          orderId: orderWithHours.order.id,
          resourceId: Number(orderWithHours.order.resourceId),
          startDate: dayjs.utc(startDate).toDate(),
          endDate: dayjs.utc(endDate).toDate(),
          workedHours: orderWithHours.totalWorkedHours,
          externalId: gestifyInvoiceResponse.docId,
          invoiceNumber: gestifyInvoiceResponse.docNumber,
          state: 'Invoiced',
          note: null,
        };

        const activity = await repository
          // @ts-ignore
          .create(activityToCreate)
          .then((response: ApiResponse<IActivity>) => {
            return response?.data;
          })
          .catch((err: AxiosError) => handleAxiosError(err));

        if (activity) return activity;
      }),
    );

    if (!activities) {
      handleGlobalError({
        code: 999,
        message: t('invoice.title'),
        description: t('invoice.errors.activitiesError'),
        responseMessage: t('invoice.errors.activitiesError'),
      });
      setLoading(false);
      return;
    }
    await getDataToInvoice();
    setLoading(false);
  };

  const getRowFromOrder = (
    orderWithHours: IDetailToInvoiceOrderWithHours,
  ): DocumentoRigaCreateType[] => {
    const days = Math.ceil((orderWithHours.totalWorkedHours / 8) * 1000) / 1000;
    return [
      {
        descrizione: `${orderReferenceLabel} ${orderWithHours.order.code} ${ofLabel} ${dayjs.utc(endDate).format('DD-MM-YYYY')}`,
      },
      {
        descrizione: `${technologyLabel} ${orderWithHours.order.technology}`,
        quantita: days,
        prezzo_vendita_netto: (orderWithHours.order.hourRevenue ?? 0) * 8,
      },
      {
        descrizione: `${orderWithHours.order.description}`,
      },
    ];
  };

  return (
    <div>
      <form
        onSubmit={(ev) => handleSubmit(ev)}
        className="size-full flex flex-col"
      >
        <div className="flex justify-between items-center content-center">
          <InvoiceFormTitle />
          <div
            onClick={handleCloseForm}
            className="bg-slate-200 text-slate-900 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 rounded-2xl p-1"
          >
            <Icon
              iconName="cancel"
              className="hover:opacity-50 hover:cursor-pointer fill-black stroke-black dark:fill-white dark:stroke-white"
            />
          </div>
        </div>
        <div className="flex flex-grow overflow-hidden flex-col border h-full justify-between py-4 mt-4 rounded-lg border-purple70">
          <Loader showLoader={loading} />
          <InvoiceFormBody
            detailToInvoice={detailToInvoice}
            startDate={startDate}
            endDate={endDate}
            provider={invoicingConfig?.provider}
          />
          <InvoiceFormButtons
            handleCloseForm={handleCloseForm}
            disabled={!invoicingConfig || alreadyInvoiced}
            disabledReason={submitDisabledReason}
            onRetry={invoicingConfigError ? reloadInvoicingConfig : undefined}
          />
        </div>
        {showPopup && <Alert type="success" message={popupMessage} />}
      </form>
    </div>
  );
};

export default InvoiceForm;
